<script setup lang="ts">
  import { computed, ref } from 'vue';

  import type { CreditMemo, Partner } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import { MonetaryText } from '@ui/components/monetary';
  import { Timestamp } from '@ui/components/time';

  import CreditMemoDrawer from '@/components/common/CreditMemoDrawer.vue';
  import CreditMemoStatusBadge from '@/components/credit-memos/CreditMemoStatusBadge.vue';
  import { PartnerQuickView } from '@/components/partners';

  const props = withDefaults(
    defineProps<{
      creditMemo: CreditMemo;
      customer?: Partner;
      showCustomer?: boolean;
    }>(),
    { showCustomer: true },
  );

  const customer = computed(() => props.customer || props.creditMemo.partner);
  const displayNumber = computed(() => props.creditMemo.number || 'Draft');
  const displayLabel = computed(() =>
    props.creditMemo.number ? `#${props.creditMemo.number}` : 'Draft',
  );
  const displayedDate = computed(() => props.creditMemo.due_date || props.creditMemo.posted_date);
  const displayedDateLabel = computed(() => (props.creditMemo.due_date ? 'Due' : 'Posted'));

  const selectedCreditMemo = ref<CreditMemo>();
  const showSheet = ref(false);

  const handleSheetOpen = () => {
    selectedCreditMemo.value = props.creditMemo;
    showSheet.value = true;
  };

  const handleSheetClose = () => {
    selectedCreditMemo.value = undefined;
    showSheet.value = false;
  };
</script>

<template>
  <div class="border-border bg-card text-card-foreground w-full relative overflow-hidden border">
    <div class="flex items-center justify-between truncate gap-4 px-4 py-3">
      <div class="flex flex-col gap-x-2 z-2 min-w-0">
        <span class="uppercase text-3xs font-bold text-muted-foreground">Credit Memo</span>

        <Button
          data-test="credit-memo-number"
          class="text-xs text-foreground font-bold truncate hover:underline cursor-pointer"
          variant="link"
          size="inline"
          :title="displayNumber"
          @click="handleSheetOpen"
        >
          {{ displayLabel }}
        </Button>
      </div>

      <div class="flex items-center gap-x-2 min-w-0">
        <CreditMemoStatusBadge :credit-memo="creditMemo" size="xs" />

        <PartnerQuickView v-if="showCustomer && customer" :partner="customer" />
      </div>
    </div>

    <div class="border-border border-t-2 border-dotted" />

    <div class="divide-border flex items-stretch divide-x divide-dotted">
      <div class="flex flex-col flex-1 px-4 py-3 gap-y-1">
        <p class="text-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground">
          Amount
        </p>

        <p class="font-mono text-xs">
          <MonetaryText
            :value="creditMemo.remaining_amount"
            :currency="creditMemo.currency"
            major-unit
            :compact="false"
          />
          <span
            v-if="creditMemo.remaining_amount !== creditMemo.net_amount"
            class="text-muted-foreground ml-1 font-normal"
          >
            of
            <MonetaryText
              :value="creditMemo.net_amount"
              :currency="creditMemo.currency"
              major-unit
              :compact="false"
            />
          </span>
        </p>
      </div>

      <div v-if="displayedDate" class="flex flex-col gap-y-1 shrink-0 px-4 py-3">
        <p class="text-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground">
          {{ displayedDateLabel }}
        </p>
        <Timestamp class="font-mono text-xs" :time="displayedDate" format="ll" date-only />
      </div>
    </div>

    <CreditMemoDrawer
      v-if="selectedCreditMemo"
      v-model:open="showSheet"
      :credit-memo="selectedCreditMemo"
      :partner="customer"
      @close="handleSheetClose"
    />
  </div>
</template>
